import { useEffect, useMemo, useReducer } from 'react';
import type { FunctionComponent, ReactNode } from 'react';

import OfflineDataContext from './OfflineDataContext';
import { getActions } from './actions';
import { initialState, reducer } from './reducer';

const OfflineDataProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(() => getActions(dispatch), [dispatch]);

  const { isOfflineEnabled } = state;
  const { setOfflineStatus } = actions;

  useEffect(() => {
    const handleStatusChange = () => {
      if (isOfflineEnabled) setOfflineStatus(!navigator.onLine);
    };

    // Listen to the online status
    window.addEventListener('online', handleStatusChange);

    // Listen to the offline status
    window.addEventListener('offline', handleStatusChange);

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [setOfflineStatus, isOfflineEnabled]);

  return (
    <OfflineDataContext.Provider value={{ actions, state }}>{children}</OfflineDataContext.Provider>
  );
};

export default OfflineDataProvider;
